import { ProcessedScanResult } from "../../components/Pages/Diary/SpaceScanner/SpaceScanner.types";

interface OptimisationScoreResult {
  score: number;
  label: string;
  explanation: string;
}

/**
 * Calculates an optimisation score based on scan results.
 * The score represents how well bookings are placed after optimisation.
 */
export const calculateOptimisationScore = (
  results: ProcessedScanResult[]
): OptimisationScoreResult => {
  const total = results.length;

  if (total === 0) {
    return {
      score: 0,
      label: "No Data",
      explanation: "No bookings were included in this scan."
    };
  }

  const lockedOrExcluded = results.filter(
    (r) =>
      r.result === "booking_locked" ||
      r.result === "booking_excluded" ||
      r.result === "booking_locked_deposit" ||
      r.result === "booking_locked_preorder" ||
      r.result === "booking_locked_manual" ||
      r.result === "booking_started" ||
      r.result === "booking_guestlist" ||
      r.result === "booking_left" ||
      r.result === "booking_waitlisted" ||
      r.result === "booking_noshow" ||
      r.shuffleLocked
  ).length;

  const movedToBetter = results.filter(
    (r) => r.moved && !r.shuffleLocked
  ).length;

  const noBetterArea = results.filter(
    (r) =>
      !r.moved &&
      !r.shuffleLocked &&
      (r.result === "no_better_area" || r.result === "preferred_area_found")
  ).length;

  const assessable = total - lockedOrExcluded;

  if (assessable === 0) {
    return {
      score: 100,
      label: "N/A",
      explanation:
        "All bookings are locked or excluded and cannot be optimised."
    };
  }

  const score = Math.round(((movedToBetter + noBetterArea) / assessable) * 100);

  const label = getScoreLabel(score);
  const optimalCount = movedToBetter + noBetterArea;
  const explanation = `${optimalCount} of ${assessable} bookings are optimally placed. ${movedToBetter} booking${movedToBetter !== 1 ? "s were" : " was"} moved to better-fitting tables.`;

  return { score, label, explanation };
};

/**
 * Returns a human-readable label for the given score.
 */
const getScoreLabel = (score: number): string => {
  if (score >= 90) return "Excellent";
  if (score >= 75) return "Good";
  if (score >= 50) return "Fair";
  return "Needs Attention";
};

/**
 * Generates a concise, human-readable explanation for why a booking was moved
 * or kept in place. Always uses structured data (areas, capacity, party size)
 * rather than raw backend debug text.
 */
export const explainBookingMove = (result: ProcessedScanResult): string => {
  if (
    !result.moved &&
    (result.result === "no_better_area" ||
      result.result === "preferred_area_found")
  ) {
    return "This booking is already optimally placed.";
  }

  return buildFallbackExplanation(result);
};

/**
 * Returns the filtered backend explanation lines for display in a
 * "More info" section. Debug-level entries are stripped out.
 * Returns an empty array when no meaningful detail is available.
 */
export const getExplanationDetails = (
  result: ProcessedScanResult
): string[] => {
  if (!result.explain || result.explain.length === 0) {
    return [];
  }

  return filterExplanationLines(result.explain);
};

/**
 * Filters verbose backend explanation steps, discarding debug-level lines
 * (overlap checks, empty-table scans) and keeping only conclusion entries.
 */
const filterExplanationLines = (lines: string[]): string[] => {
  const debugPatterns = [
    /^Checking Table/i,
    /Area isn't empty/i,
    /Areas aren't empty/i,
    /Area isn't available/i,
    /doesn't overlap/i,
    /^Found booking/i,
    /^Booking DMN-/i,
    /^overlap/i
  ];

  return lines.filter(
    (line) => !debugPatterns.some((pattern) => pattern.test(line.trim()))
  );
};

/**
 * Constructs a fallback explanation from available booking and area data.
 */
const buildFallbackExplanation = (result: ProcessedScanResult): string => {
  if (!result.moved) {
    return getNotMovedExplanation(result);
  }

  const partySize = result.partySize ?? result.booking.num_people;
  const previousAreaName =
    result.previousAreas?.[0]?.name ?? "their current table";
  const newAreaName = result.newAreas?.[0]?.name ?? "a new table";

  const previousCapacity = result.previousAreaCapacity;
  const newCapacity = result.areaCapacity;

  if (previousCapacity && newCapacity) {
    return `Moving this ${partySize}-person party from ${previousAreaName} (seats ${previousCapacity}) to ${newAreaName} (seats ${newCapacity}) frees up ${previousAreaName} for larger bookings.`;
  }

  return `Consider moving this ${partySize}-person party from ${previousAreaName} to ${newAreaName} for a better fit.`;
};

/**
 * Returns an explanation for bookings that are not being moved.
 */
const getNotMovedExplanation = (result: ProcessedScanResult): string => {
  switch (result.result) {
    case "booking_locked":
    case "booking_locked_deposit":
    case "booking_locked_preorder":
    case "booking_locked_manual":
    case "booking_excluded":
      return "This booking is locked and cannot be moved.";
    case "booking_started":
      return "This booking has already started and cannot be moved.";
    case "booking_guestlist":
      return "Guestlist bookings are excluded from optimisation.";
    case "booking_left":
      return "This booking has left the venue.";
    case "booking_waitlisted":
      return "Waitlisted bookings are not included in optimisation.";
    case "booking_noshow":
      return "This booking is marked as a no-show.";
    case "area_closed":
      return "The target area is currently closed.";
    case "unassigned_no_tables_configured":
      return "No tables are configured for this booking type.";
    case "unassigned_table_too_small":
      return "No table is large enough for this party size.";
    case "unassigned_rule_conflict":
      return "Booking rules prevent table assignment.";
    case "unassigned_no_availability":
      return "No tables are available at this time.";
    default:
      return "No changes suggested for this booking.";
  }
};
